/**
 * Synthesised sound effects via Web Audio. No asset files, works offline,
 * nothing to license. The context is created on the first user gesture,
 * which iOS requires. Muting is remembered per device.
 */
import type { GameEvent } from '@horde/engine';

const KEY = 'horde:muted';
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = (() => {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
})();

export function isMuted(): boolean {
  return muted;
}

export function setMuted(on: boolean): void {
  muted = on;
  try {
    localStorage.setItem(KEY, on ? '1' : '0');
  } catch {
    // ignore
  }
  if (master) master.gain.value = on ? 0 : 1;
}

/** Call from any pointer/keyboard handler so playback is allowed later. */
export function unlockAudio(): void {
  if (ctx) {
    if (ctx.state === 'suspended') void ctx.resume();
    return;
  }
  const AC =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return;
  ctx = new AC();
  master = ctx.createGain();
  master.gain.value = muted ? 0 : 1;
  master.connect(ctx.destination);
}

function now(): number {
  return ctx!.currentTime;
}

function tone(
  freq: number,
  t0: number,
  dur: number,
  opts: { type?: OscillatorType; gain?: number; slideTo?: number } = {},
) {
  if (!ctx || !master) return;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = opts.type ?? 'sine';
  o.frequency.setValueAtTime(freq, t0);
  if (opts.slideTo) o.frequency.exponentialRampToValueAtTime(opts.slideTo, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(opts.gain ?? 0.2, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(master);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
}

function noise(
  t0: number,
  dur: number,
  opts: { gain?: number; lowpass?: number; highpass?: number } = {},
) {
  if (!ctx || !master) return;
  const n = Math.floor(ctx.sampleRate * dur);
  const buf = ctx.createBuffer(1, n, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const g = ctx.createGain();
  g.gain.value = opts.gain ?? 0.15;
  let node: AudioNode = src;
  if (opts.lowpass) {
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = opts.lowpass;
    node.connect(f);
    node = f;
  }
  if (opts.highpass) {
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.value = opts.highpass;
    node.connect(f);
    node = f;
  }
  node.connect(g).connect(master);
  src.start(t0);
}

/** Soft dice rattle: a few muffled taps. */
function rattle(t: number): void {
  for (let i = 0; i < 4; i++)
    noise(t + i * 0.04, 0.04, { gain: 0.045, lowpass: 2600, highpass: 300 });
}

export type Sfx =
  | 'dice'
  | 'hit'
  | 'miss'
  | 'capture'
  | 'loss'
  | 'bite'
  | 'overrun'
  | 'card'
  | 'purge'
  | 'win'
  | 'defeat'
  | 'tap';

export function play(sfx: Sfx): void {
  if (!ctx || !master || muted) return;
  if (ctx.state === 'suspended') void ctx.resume();
  const t = now();
  switch (sfx) {
    case 'tap':
      tone(880, t, 0.05, { gain: 0.05 });
      break;
    case 'dice':
      rattle(t);
      break;
    case 'hit':
      // Your roll won the exchange: a light, bright tick after the rattle.
      rattle(t);
      tone(1046, t + 0.2, 0.07, { type: 'triangle', gain: 0.06 });
      break;
    case 'miss':
      // Your roll lost the exchange: a soft, rounded fall. Never harsh.
      rattle(t);
      tone(330, t + 0.2, 0.22, { type: 'sine', gain: 0.09, slideTo: 196 });
      break;
    case 'card':
      noise(t, 0.12, { gain: 0.1, lowpass: 2400, highpass: 400 });
      tone(1200, t + 0.1, 0.06, { gain: 0.05 });
      break;
    case 'capture':
      tone(523, t, 0.12, { type: 'triangle', gain: 0.15 });
      tone(784, t + 0.1, 0.18, { type: 'triangle', gain: 0.15 });
      break;
    case 'purge':
      tone(660, t, 0.08, { type: 'square', gain: 0.06 });
      noise(t, 0.1, { gain: 0.08, lowpass: 1200 });
      break;
    case 'loss':
      tone(330, t, 0.15, { type: 'triangle', gain: 0.12, slideTo: 220 });
      break;
    case 'bite':
      tone(90, t, 0.18, { type: 'sawtooth', gain: 0.18, slideTo: 60 });
      noise(t, 0.15, { gain: 0.12, lowpass: 500 });
      break;
    case 'overrun':
      tone(70, t, 0.6, { type: 'sawtooth', gain: 0.2, slideTo: 40 });
      noise(t, 0.5, { gain: 0.15, lowpass: 300 });
      break;
    case 'win':
      [523, 659, 784, 1046].forEach((f, i) =>
        tone(f, t + i * 0.12, 0.3, { type: 'triangle', gain: 0.14 }),
      );
      break;
    case 'defeat':
      [392, 311, 233].forEach((f, i) =>
        tone(f, t + i * 0.25, 0.5, { type: 'sawtooth', gain: 0.1 }),
      );
      noise(t + 0.6, 0.8, { gain: 0.12, lowpass: 250 });
      break;
  }
}

/** Pick the one sound that best describes a batch of events, from `me`'s point of view. */
export function sfxFor(events: GameEvent[], me: string | null, attackerIsMe = false): Sfx | null {
  let pick: Sfx | null = null;
  const rank: Record<Sfx, number> = {
    tap: 0,
    card: 1,
    dice: 2,
    hit: 2,
    miss: 2,
    purge: 3,
    loss: 4,
    capture: 5,
    bite: 6,
    overrun: 7,
    win: 9,
    defeat: 9,
  };
  const consider = (s: Sfx) => {
    if (!pick || rank[s] > rank[pick]) pick = s;
  };
  for (const e of events) {
    switch (e.type) {
      case 'diceRolled':
        if (!attackerIsMe) consider('dice');
        else consider(e.attackerLosses > e.defenderLosses ? 'miss' : 'hit');
        break;
      case 'cardDrawn':
        consider('card');
        break;
      case 'zombiesRemoved':
        consider('purge');
        break;
      case 'territoryCaptured':
        consider(e.to === me ? 'capture' : e.from === me ? 'loss' : 'purge');
        break;
      case 'bitten':
        if (me && e.territoryId) consider('bite');
        break;
      case 'overrun':
        consider(e.previousOwner === me ? 'overrun' : 'bite');
        break;
      case 'gameEnded':
        consider(e.outcome.kind === 'victory' && e.outcome.winner === me ? 'win' : 'defeat');
        break;
      default:
        break;
    }
  }
  return pick;
}
